import { useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, TrendingUp, ShoppingCart, DollarSign, Users, CreditCard, Package, RefreshCw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { useCampaignStats, TIMEFRAMES } from "@/hooks/useCampaignStats";
import { useAdminStatsGeneration } from "@/hooks/useAdminStatsGeneration";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

const CHART_COLORS = [
  "hsl(var(--secondary))",
  "hsl(var(--primary))",
  "hsl(var(--info))",
  "hsl(var(--warning))",
  "hsl(var(--destructive))",
];

export const CampaignStatsCard = () => {
  const { timeframe, setTimeframe, stats, chartData, isLoading, timeframeLabel, generatedAt, isRefreshing } = useCampaignStats();
  const { generateStats, isGenerating } = useAdminStatsGeneration();

  const hasNoCache = !isLoading && !stats;

  // Auto-generate stats on first load if no cache exists
  useEffect(() => {
    if (hasNoCache && !isGenerating("campaign")) {
      generateStats("campaign");
    }
  }, [hasNoCache]);

  return (
    <Card>
      <CardHeader className="py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-4 w-4 text-secondary" />
              Campaign Stats
            </CardTitle>
            <CardDescription className="text-xs">
              {timeframeLabel}
              {generatedAt && (
                <span className="ml-2 text-muted-foreground">
                  · Updated {formatDistanceToNow(new Date(generatedAt))} ago
                  {isRefreshing && <span className="ml-1 text-xs opacity-70">(refreshing...)</span>}
                </span>
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2"
              onClick={() => generateStats("campaign")}
              disabled={isGenerating("campaign") || isRefreshing}
            >
              <RefreshCw className={cn("h-3.5 w-3.5", (isGenerating("campaign") || isRefreshing) && "animate-spin")} />
            </Button>
            <div className="flex gap-0.5">
              {TIMEFRAMES.map(({ value, label }) => (
                <Button
                  key={value}
                  variant={timeframe === value ? "secondary" : "ghost"}
                  size="sm"
                  className={cn("h-6 px-2 text-xs", timeframe === value && "font-bold")}
                  onClick={() => setTimeframe(value)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="py-3 space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : hasNoCache ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-muted-foreground mb-4">No cached stats available</p>
            <Button onClick={() => generateStats("campaign")} disabled={isGenerating("campaign")}>
              {isGenerating("campaign") ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                "Generate Stats"
              )}
            </Button>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-1.5">
                <ShoppingCart className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Purchases:</span>
                <span className="font-semibold">{stats?.totalPurchases ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <DollarSign className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Revenue:</span>
                <span className="font-semibold">£{stats?.totalRevenue?.toFixed(2) ?? "0.00"}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CreditCard className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Credits Granted:</span>
                <span className="font-semibold">{stats?.totalCreditsGranted ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Unique Buyers:</span>
                <span className="font-semibold">{stats?.uniqueUsers ?? 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Package className="h-3.5 w-3.5 text-secondary" />
                <span className="text-muted-foreground">Active Campaigns:</span>
                <span className="font-semibold">{stats?.activeCampaigns ?? 0}</span>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              {/* Purchases Over Time */}
              <div className="lg:col-span-2">
                <h4 className="text-xs font-medium text-muted-foreground mb-1">Purchases Over Time</h4>
                <div className="h-28">
                  {chartData.purchasesOverTime.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData.purchasesOverTime} margin={{ left: 0, right: 4 }}>
                        <XAxis
                          dataKey="date"
                          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 8 }}
                          interval="preserveStartEnd"
                          tickLine={false}
                          axisLine={{ stroke: "hsl(var(--border))" }}
                        />
                        <YAxis
                          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 8 }}
                          tickLine={false}
                          axisLine={{ stroke: "hsl(var(--border))" }}
                          width={24}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: "6px",
                            fontSize: "11px",
                          }}
                          formatter={(value: number, name: string) => [
                            name === "revenue" ? `£${value.toFixed(2)}` : value,
                            name === "revenue" ? "Revenue" : "Purchases"
                          ]}
                        />
                        <Bar dataKey="purchases" fill="hsl(var(--secondary))" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No data</div>
                  )}
                </div>
              </div>

              {/* Package Breakdown */}
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-1">Top Packages</h4>
                <div className="h-28">
                  {chartData.packageBreakdown.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartData.packageBreakdown}
                          dataKey="count"
                          nameKey="name"
                          cx="30%"
                          cy="50%"
                          outerRadius={35}
                        >
                          {chartData.packageBreakdown.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Legend
                          layout="vertical"
                          align="right"
                          verticalAlign="middle"
                          wrapperStyle={{ fontSize: "10px", paddingLeft: "8px" }}
                          formatter={(value, entry: any) => {
                            const name = value.length > 12 ? value.substring(0, 12) + "..." : value;
                            return `${name}: ${entry.payload.count}`;
                          }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: "6px",
                            fontSize: "11px",
                          }}
                          formatter={(value: number, name: string, props: any) => [
                            `${value} (£${props.payload.revenue.toFixed(2)})`,
                            props.payload.name
                          ]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No data</div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
